import { SampleFile } from "projen";
import { NodeProject } from "projen/lib/javascript";

/**
 * Wire Playwright E2E tests into a {@link NodeProject}.
 *
 * Adds `@playwright/test` as a dev dep, registers the standard
 * `e2e:install` / `e2e` / `e2e:ui` / `e2e:report` projen tasks (with
 * matching package scripts), emits a minimal `playwright.config.ts`
 * pointing at `http://localhost:5173` (Vite's default dev port), and
 * gitignores Playwright's output directories.
 *
 * Designed to be called against a `ReactViteSiteProject`, but accepts
 * any `NodeProject` — the integration is intentionally framework-light
 * so it can layer onto adjacent React + Vite scaffolds without
 * subclassing.
 *
 * @param project - The project to wire Playwright into.
 */
export function addPlaywright(project: NodeProject): void {
  project.addDevDeps("@playwright/test");

  /**
   * Tasks.
   */
  project.tasks.addTask("e2e:install", {
    description: "Install Playwright browsers",
    steps: [{ exec: "npx playwright install --with-deps" }],
  });
  project.tasks.addTask("e2e", {
    description: "Run Playwright E2E tests (headless)",
    steps: [{ exec: "npx playwright test" }],
  });
  project.tasks.addTask("e2e:ui", {
    description: "Run Playwright in UI mode",
    steps: [{ exec: "npx playwright test --ui" }],
  });
  project.tasks.addTask("e2e:report", {
    description: "Open last HTML report",
    steps: [{ exec: "npx playwright show-report" }],
  });

  /**
   * Package scripts.
   */
  project.setScript("e2e:install", "npx projen e2e:install");
  project.setScript("e2e", "npx projen e2e");
  project.setScript("e2e:ui", "npx projen e2e:ui");
  project.setScript("e2e:report", "npx projen e2e:report");

  /**
   * Config + ignores. Defaults `baseURL` to Vite's local dev server,
   * overridable via the `PLAYWRIGHT_BASE_URL` env var.
   */
  new SampleFile(project, "playwright.config.ts", {
    contents: `import { defineConfig, devices } from '@playwright/test';

export default defineConfig({
  testDir: './tests',
  reporter: [['html', { open: 'never' }]],
  use: {
    baseURL: process.env.PLAYWRIGHT_BASE_URL || 'http://localhost:5173',
    trace: 'on-first-retry',
  },
  projects: [{ name: 'chromium', use: { ...devices['Desktop Chrome'] } }],
});
`,
  });

  project.gitignore.addPatterns("test-results/", "playwright-report/");
}
